import React, { useEffect, useState } from 'react';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  Box, Grid, Typography, Button, Container, CircularProgress,
  Alert, TextField, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions,
  InputAdornment, Card, CardContent
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { ClearIcon, DatePicker } from '@mui/x-date-pickers';
import { useAppDispatch, useAppSelector } from 'reduxStore/hooks';
import { tokenList } from 'reduxStore/tokenList/tokenListSlice';
import Footer from './Footer';
import SearchIcon from '@mui/icons-material/Search';
import { resetAddVisitState } from 'reduxStore/addNewVisit/addNewVisitSlice';
import { usePrintTokenList } from 'components/PrintableTokenList';

const TokenList: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [dataGridArray, setDataGridArray] = React.useState([] as any[]);
  const [header, setHeader] = React.useState({} as Record<string, string>);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('day'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().endOf('day'));
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState<any>(null);
  const [tokenSearchValue, setTokenSearchValue] = useState('');

  const tokenListResponse = useAppSelector((state) => state.tokenListRes);
  const toHeaderName = (field: string) =>
    field
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim()
      .replace(/\s+/g, ' ');

  useEffect(() => {
    if (tokenListResponse?.response?.data !== undefined) {
      const rows = tokenListResponse?.response?.data;
      const fieldToHeaderMap: Record<string, string> = rows.length
        ? Object.keys(rows[0]).reduce((acc, key) => {
          acc[key] = toHeaderName(key);
          return acc;
        }, {} as Record<string, string>)
        : {};
      setHeader(fieldToHeaderMap);
      setDataGridArray(rows);
    }
  }, [tokenListResponse]);

  const handleStartDateChange = (newValue: Dayjs | null) => {
    setStartDate(newValue);
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    if (newValue && startDate && newValue.isAfter(startDate.add(15, 'minutes'))) {
      setEndDate(newValue);
    } else if (newValue && startDate) {
      setEndDate(startDate.add(15, 'minutes'));
    } else {
      setEndDate(newValue);
    }
  };

  const fetchTokenList = () => {
    if (startDate && endDate) {
      dispatch(tokenList({
        doctype: 'Token',
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD HH:mm')
      }));
    }
  };

  useEffect(() => {
    fetchTokenList();
  }, [startDate, endDate]);

  const addNewVisit = () => {
    dispatch(resetAddVisitState());
    navigate('/addnewvisit');
  };

  const handleRefresh = () => {
    fetchTokenList();
  };

  const handleView = (row: any) => {
    setSelectedToken(row);
    setViewDialogOpen(true);
  };

  const handleEdit = (row: any) => {
    navigate(`/token/edit/${row.token_number}`, {
      state: { tokenData: row }
    });
  };

  const handleCloseDialog = () => {
    setViewDialogOpen(false);
    setSelectedToken(null);
  };

  const filteredDataGridArray = React.useMemo(() => {
    if (!tokenSearchValue.trim()) {
      return dataGridArray;
    }

    return dataGridArray.filter((row) => {
      const tokenNumber = row.token_number?.toString().toLowerCase() || '';
      const searchLower = tokenSearchValue.toLowerCase();
      return tokenNumber.includes(searchLower);
    });
  }, [dataGridArray, tokenSearchValue]);

  const { PrintIconButton } = usePrintTokenList(
    filteredDataGridArray,
    header,
    startDate,
    endDate,
    toHeaderName
  );

  const handleClearSearch = () => {
    setTokenSearchValue('');
  };

  const renderCellWithDefault = (params: any, defaultValue = '---') => {
    return params.value !== null && params.value !== undefined && params.value !== ''
      ? params.value
      : defaultValue;
  };

  const columns: GridColDef[] = [
    ...Object.keys(header).map((field) => {
      const baseColumn: GridColDef = {
        field,
        headerName: header[field],
        minWidth: 150,
        flex: 1,
        headerAlign: 'center',
        align: 'center',
        headerClassName: 'super-app-theme--header',
        renderCell: (params) => renderCellWithDefault(params),
      };

      if (field === 'token_number') {
        return {
          ...baseColumn,
          minWidth: 120,
          renderCell: (params: any) => (
            <Chip
              label={`${params.value}`}
              color="primary"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          ),
        };
      }

      if (field === 'docstatus') {
        return {
          ...baseColumn,
          minWidth: 130,
          renderCell: (params: any) => {
            const status = params.value;
            const statusConfig: Record<number, { label: string; color: any }> = {
              0: { label: 'Draft', color: 'default' },
              1: { label: 'Submitted', color: 'success' },
              2: { label: 'Cancelled', color: 'error' },
            };
            const config = statusConfig[status] || { label: 'Unknown', color: 'default' };
            return (
              <Chip
                label={config.label}
                color={config.color}
                size="small"
                variant="filled"
              />
            );
          },
        };
      }

      if (field === 'phone_number') {
        return {
          ...baseColumn,
          minWidth: 140,
          renderCell: (params: any) => (
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {params.value}
            </Typography>
          ),
        };
      }

      if (field === 'in_time') {
        return {
          ...baseColumn,
          minWidth: 180,
          renderCell: (params: any) => (
            <Typography variant="body2" color="text.secondary">
              {dayjs(params.value).format('MMM DD, YYYY HH:mm')}
            </Typography>
          ),
        };
      }

      return baseColumn;
    }),
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      flex: 0.5,
      sortable: false,
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleView(params.row);
            }}
            title="View Details"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="secondary"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(params.row);
            }}
            title="Edit Token"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth='xl'>
      <Grid
        container
        direction="row"
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
        }}
        mb={2}
        spacing={1}
      >
        <Grid item>
          <Typography variant="h5">
            Token Management
          </Typography>
        </Grid>
        <Grid item>
          <Chip label={tokenListResponse?.response?.data?.length} />
        </Grid>
        <Grid item>
          <IconButton
            color="primary"
            onClick={handleRefresh}
          >
            <RefreshIcon />
          </IconButton>
        </Grid>
        <Grid item>
          <PrintIconButton />
        </Grid>
      </Grid>

      {/* Card for Department Stats - Aligned from Token Management to Add Token Button */}
      <Card
        sx={{
          mb: 2,
          boxShadow: 1,
          border: '1px solid #e0e0e0'
        }}
      >
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Pediatric Cardiology */}
            <Grid item xs={12} sm={3} md={3}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    Pediatric Cardiology
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled
                    label="Check In"
                    defaultValue="0"
                    size='small'
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled
                    label="Check Out"
                    defaultValue="0"
                    size='small'
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Pediatric Cardiac Surgery */}
            <Grid item xs={12} sm={3} md={3}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    Pediatric Cardiac Surgery
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled
                    label="Check In"
                    defaultValue="0"
                    size='small'
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled
                    label="Check Out"
                    defaultValue="0"
                    size='small'
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Mother & Child */}
            <Grid item xs={12} sm={3} md={3}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    Mother & Child
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled
                    label="Check In"
                    defaultValue="0"
                    size='small'
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled
                    label="Check Out"
                    defaultValue="0"
                    size='small'
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
            {/* Blood Bank */}
            <Grid item xs={12} sm={3} md={3}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    Blood Bank
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled
                    label="Check In"
                    defaultValue="0"
                    size='small'
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled
                    label="Check Out"
                    defaultValue="0"
                    size='small'
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ minHeight: '71vh', width: '100%' }}>
          <Grid
            container
            direction="row"
            spacing={2}
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
            mb={2}
          >
            <Grid item xs={12} sm={6} md={6} container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      required: true
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      required: true,
                    }
                  }}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Search Token Number"
                value={tokenSearchValue}
                onChange={(e) => setTokenSearchValue(e.target.value)}
                size="small"
                fullWidth
                placeholder="Enter token number..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: tokenSearchValue && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClearSearch}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <Button
                variant="contained"
                color="primary"
                sx={{ textTransform: 'none' }}
                onClick={addNewVisit}
                fullWidth
                size='small'
              >
                Add Token
              </Button>
            </Grid>
          </Grid>

          {tokenListResponse.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {tokenListResponse.error}
            </Alert>
          )}

          {tokenListResponse.loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '400px'
              }}
            >
              <CircularProgress />
            </Box>
          ) : filteredDataGridArray.length > 0 ? (
            <Box
              sx={{
                '& .super-app-theme--header': {
                  backgroundColor: '#1a5f23',
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                },
              }}
            >
              <DataGrid
                rows={filteredDataGridArray}
                columns={columns}
                getRowId={(row) => row.name}
                autoHeight
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 10 }
                  }
                }}
                pageSizeOptions={[5, 10, 20, 50]}
                disableRowSelectionOnClick
                disableColumnMenu
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector

                sx={{
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #f0f0f0',
                    padding: '12px 16px',
                  },
                  '& .MuiDataGrid-cell:hover': {
                    color: '#1976d2',
                    cursor: 'pointer',
                  },
                  '& .MuiDataGrid-row': {
                    '&:nth-of-type(odd)': {
                      backgroundColor: '#fafafa',
                    },
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      transition: 'background-color 0.2s ease',
                    },
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    borderBottom: '2px solid #1a5f23',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '2px solid #e0e0e0',
                    backgroundColor: '#fafafa',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    minHeight: '200px',
                  },
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '300px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                backgroundColor: '#fafafa'
              }}
            >
              <Typography variant="h6" color="text.secondary">
                No tokens found for the selected date range
              </Typography>
            </Box>
          )}
        </Box>

        <Dialog
          open={viewDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Token Details
          </DialogTitle>
          <DialogContent dividers>
            {selectedToken && (
              <Grid container spacing={2}>
                {Object.entries(selectedToken).map(([key, value]) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {toHeaderName(key)}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {key === 'in_time'
                        ? dayjs(value as string).format('MMM DD, YYYY HH:mm')
                        : String(value)}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Close
            </Button>
            <Button
              onClick={() => {
                handleCloseDialog();
                handleEdit(selectedToken);
              }}
              variant="contained"
              color="primary"
            >
              Edit Token
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
      <Footer />
    </Container>
  );
};

export default TokenList;